import { AxiosInstance } from 'axios';
import _ from 'lodash';
import moment from 'moment';
import { createAxiosInstance } from '../constants/AWS';
import { DisplayPayload } from '../constants/UserListField';
import { waitTimeMs } from '../utils/commonUtil';

const API_VERSION = 'v2';

export class MoffRakuAPI {
  /** インスタンス */
  private static _instance: MoffRakuAPI;
  private axiosInstance!: AxiosInstance;

  // .envの設定
  private API_BASE_URL = String(process.env.REACT_APP_MOFF_API_URL);

  /** インスタンスの取得 */
  public static get instance(): MoffRakuAPI | null {
    // _inctanceが存在しない場合に、new MoffRakuAPI()を実行する。
    if (!this._instance) {
      this._instance = new MoffRakuAPI();
      // Cognito JWTでの認証はcreateAxiosInstance内のrequest interceptorで毎回最新トークンを付与する.
      const axiosInstance = createAxiosInstance(this._instance.API_BASE_URL, null, null);

      if (axiosInstance) {
        this._instance.axiosInstance = axiosInstance;
      } else {
        return null;
      }
    }

    // 生成済みのインスタンスを返す
    return this._instance;
  }

  // 後方互換のため残置。JWTはrequest interceptorで毎リクエスト時に自動付与される (Issue #842).
  public async setAxiosInstanceWithJwtToken(): Promise<void> {
    return;
  }

  /**
   * モフらく計画書の最新作成日を取得するAPI
   *
   * @param {string} userId
   * @param {Function} addDisplayDate
   * @param {number} intervalTimeMs
   * @returns
   */
  public async getMoffRakuLastDataAPI(
    userId: string,
    addDisplayDate: (payload: DisplayPayload) => void,
    intervalTimeMs = 500,
  ): Promise<string> {
    try {
      // JWT tokenを設定
      await this.setAxiosInstanceWithJwtToken();

      // 現在の日付から1年前までの期間で計画書を取得
      const toDate = moment().format('YYYY-MM-DD');
      const fromDate = moment().subtract(1, 'year').format('YYYY-MM-DD');

      const response = await this.axiosInstance.get(
        `${API_VERSION}/moff_raku/plans/${userId}/from/${fromDate}/to/${toDate}`,
      );
      const plans = response.data.data;

      // 計画書がない場合は空の日付を返す
      if (!plans || plans.length === 0) {
        addDisplayDate({ user_id: userId, displayDate: '' });
        return '';
      }

      // 作成日の降順でソート
      const sortedPlans = _.sortBy(plans, (plan) => {
        return moment(plan.created_at).valueOf() * -1;
      });

      // 最新の計画書の作成日を取得
      const latestDate = moment(sortedPlans[0].created_at).format('YYYY-MM-DD');

      // 表示用の日付を設定
      addDisplayDate({ user_id: userId, displayDate: latestDate });

      // 502エラーを防ぐためapiリクエストの間隔を開ける
      await waitTimeMs(intervalTimeMs);

      return latestDate;
    } catch (error) {
      console.error('Error fetching moff raku last date:', error);
      // エラーが発生した場合でも、ローディングを止めるために空の日付を設定
      addDisplayDate({ user_id: userId, displayDate: '' });
      return '';
    }
  }
}

export const createMoffRakuAPIInstance = (): [boolean, MoffRakuAPI | null, any] => {
  // MoffRakuAPIインスタンスが存在しない場合、エラーアクションを返す
  return MoffRakuAPI.instance === null
    ? [
        true,
        null,
        {
          type: 'MOFF_RAKU_API_ERROR',
          error: 'APIインスタンスの取得に失敗しました',
        },
      ]
    : [false, MoffRakuAPI.instance, null];
};
